import type { FC, ReactNode } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Popup } from '@atlaskit/popup/compositional/popup';
import { PopupContent } from '@atlaskit/popup/compositional/popup-content';
import { PopupTrigger } from '@atlaskit/popup/compositional/popup-trigger';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { Divider } from '@atlaskit/side-nav-items/menu-section';

import { useCompanyHubName } from '@confluence/company-hub-utils/entry-points/useCompanyHubName.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

import { CompanyHubItem } from './GlobalItems/CompanyHubItem/CompanyHubItem.tsx';
import { i18n } from './globalNavigationTranslations.ts';
import { useIconByMenuId, MoreIconComponent } from './GlobalNavigationIcons.tsx';
import type { MoreMenuItemConfig } from './useMenuItems.ts';

const AddFeaturesSidebarSpotlightLoader = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AddFeaturesSidebarSpotlight" */
				'@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/AddFeaturesSidebarSpotlight.ts'
			)
		).AddFeaturesSidebarSpotlight,
});

type MoreMenuItemProps = {
	moreMenuItems: MoreMenuItemConfig;
	isOpen: boolean;
	toggleMenu: () => void;
	onClick: (menuId: MenuId) => void;
	peekingId?: MenuId;
	isCollapsed?: boolean;
};

export const MoreMenuItem: FC<MoreMenuItemProps> = ({
	moreMenuItems,
	isOpen,
	toggleMenu,
	onClick,
	peekingId,
	isCollapsed,
}) => {
	const companyHubName = useCompanyHubName();
	const iconByMenuId = useIconByMenuId();

	if (!moreMenuItems || moreMenuItems?.length <= 0) {
		return null;
	}
	return (
		<Popup isOpen={isOpen}>
			<AddFeaturesSidebarSpotlightLoader isCollapsed={isCollapsed}>
				<PopupTrigger>
					{({
						ref,
						'aria-controls': ariaControls,
						'aria-expanded': ariaExpanded,
						'aria-haspopup': ariaHasPopup,
					}) => (
						<ButtonMenuItem
							ref={ref}
							aria-controls={ariaControls}
							aria-expanded={ariaExpanded}
							aria-haspopup={ariaHasPopup}
							elemBefore={MoreIconComponent}
							onClick={toggleMenu}
						>
							<FormattedMessage {...i18n.more} />
						</ButtonMenuItem>
					)}
				</PopupTrigger>
			</AddFeaturesSidebarSpotlightLoader>

			{/* Adding listitem role as the parent is a list. Not using the `li` element so it doesn't inherit default styles. */}
			<Box xcss={popupContentContainerStyles} role="listitem">
				<PopupContent
					placement="bottom-start"
					// Offset the top margin of the menu section.
					// This will keep an even spacing of menu items between those inside the dropdown and those above,
					// and keep the icons aligned
					offset={[-4, 4]}
					onClose={toggleMenu}
					shouldRenderToParent
					strategy="absolute"
					shouldFitContainer
				>
					{() => {
						// The number of visible / non-null menu items so that proper dividers can be added.
						let nonNullMenuItemCount = 0;

						const menuItems = moreMenuItems.map(({ menuId, href, isAppShortcut }) => {
							if (peekingId && menuId === peekingId) {
								return null; //if an item is peeking, do not render it in this menu
							}

							++nonNullMenuItemCount;

							if (menuId === 'companyHub') {
								return (
									<>
										{/* The app shortcuts is visually divided from the preceeding global items: */}
										{nonNullMenuItemCount !== 1 && <Divider />}
										<CompanyHubItem
											key={menuId}
											onClick={() => onClick(menuId)}
											setIsCustomizeSidebarDialogVisible={() => {}}
										/>
									</>
								);
							}

							let node: ReactNode;
							if (href) {
								node = (
									<LinkMenuItem
										href={href}
										onClick={() => onClick(menuId)}
										elemBefore={iconByMenuId[menuId]}
										key={menuId}
										target={isAppShortcut ? '_blank' : undefined}
									>
										<FormattedMessage
											{...i18n[menuId]}
											values={menuId === 'companyHubShortcut' ? { companyHubName } : {}}
										/>
									</LinkMenuItem>
								);
							} else {
								node = (
									<ButtonMenuItem
										onClick={() => onClick(menuId)}
										elemBefore={iconByMenuId[menuId]}
										key={menuId}
									>
										<FormattedMessage
											{...i18n[menuId]}
											values={menuId === 'companyHubShortcut' ? { companyHubName } : {}}
										/>
									</ButtonMenuItem>
								);
							}

							// The item "Customize sidebar" is visually divided from the preceeding global items and/or app shortcuts:

							if (node && nonNullMenuItemCount !== 1 && menuId === 'customize') {
								node = (
									<>
										<Divider />
										{node}
									</>
								);
							}

							return node;
						});

						// PopupContent has an `xcss` prop, but it casts its value to `string` and thus isn't suitable.
						return (
							<Box xcss={popupContentStyles} role="list">
								{menuItems}
							</Box>
						);
					}}
				</PopupContent>
			</Box>
		</Popup>
	);
};

const popupContentContainerStyles = xcss({
	position: 'relative',
	marginInline: 'space.negative.050',
	height: '0',
});

const popupContentStyles = xcss({
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
});
